import React, { useEffect, useState } from 'react';
import { t } from 'ttag';
import { Heading } from '@components';
import quickHelpLinks from './quickHelpLinks';
import ReturnIcon from './icons/ReturnIcon';
import TrackAndTraceIcon from './icons/TrackAndTraceIcon';

function useFunctionalConsent() {
  const [hasConsent, setHasConsent] = useState(
    () =>
      !!window?.CookieInformation?.getConsentGivenFor?.('cookie_cat_functional')
  );

  useEffect(() => {
    if (hasConsent) return undefined;
    const onConsent = () => {
      if (
        window?.CookieInformation?.getConsentGivenFor?.('cookie_cat_functional')
      ) {
        setHasConsent(true);
      }
    };
    window.addEventListener('CookieInformationConsentGiven', onConsent);
    return () =>
      window.removeEventListener('CookieInformationConsentGiven', onConsent);
  }, [hasConsent]);

  return hasConsent;
}

function QuickHelpBox({ title, description, Icon, link, ArrowIcon }) {
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex max-w-xs flex-col items-center rounded-xl bg-white p-4 pb-12 pt-12 text-black"
    >
      <div className="mb-6 flex h-12 w-12 items-center justify-center">
        <Icon className="h-12 w-12 text-black" />
      </div>
      <div className="leading-1 text-center text-base font-bold">
        {t(title)}
      </div>
      <p className="mb-4 max-w-[10rem] text-center text-sm leading-none text-dark-gray md:mb-12">
        {t(description)}
      </p>
      <span className="absolute bottom-4 right-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-green to-[#E0F65A] p-1 text-black transition-opacity ">
        {ArrowIcon}
      </span>
    </a>
  );
}

export default function QuickHelp() {
  const hasFunctionalConsent = useFunctionalConsent();
  const languageCode = window?.config?.language_code;
  // German is shared across markets (de-AT, de-DE, ...), so distinguish by
  // country segment of iso_language_code to pick the right per-market links.
  const linksKey =
    languageCode === 'de'
      ? window?.config?.iso_language_code?.split('-').pop()?.toLowerCase()
      : languageCode;
  const links = quickHelpLinks[linksKey];

  if (!links) return null;

  const quickHelpData = [
    {
      title: t`Return package`,
      description: t`Steps to return your item`,
      Icon: ReturnIcon,
      link: links?.return,
      ArrowIcon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-arrow-right"
        >
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      )
    },
    {
      title: t`Track a package`,
      description: t`Enter the order number from your order confirmation`,
      Icon: TrackAndTraceIcon,
      link: links?.trackAndTrace,
      ArrowIcon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-arrow-up-right"
        >
          <path d="M7 7h10v10" />
          <path d="M7 17 17 7" />
        </svg>
      )
    }
  ];

  return (
    <section className="max-w-screen-sm rounded-xl">
      <Heading as="h4" className="mb-2 break-words text-2xl normal-case">
        {t`Quick help`}
      </Heading>
      {window?.config?.faq_iframe_enabled && hasFunctionalConsent && (
        <iframe
          src="https://cust-unisportiframe.web.app/"
          title={t`Help`}
          className="mb-4 h-[75vh] w-full rounded-xl border-0"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          referrerPolicy="no-referrer-when-downgrade"
          loading="lazy"
        />
      )}
      <div className="grid grid-cols-2 gap-2 md:gap-4">
        {quickHelpData.map(({ title, description, Icon, link, ArrowIcon }) => (
          <QuickHelpBox
            key={title}
            title={title}
            description={description}
            Icon={Icon}
            link={link}
            ArrowIcon={ArrowIcon}
          />
        ))}
      </div>
    </section>
  );
}
